import { z } from 'zod';

export const FileKindSchema = z.enum([
  'image',
  'spreadsheet',
  'document',
  'data',
  'chart-spec',
  'other',
]);
export type FileKind = z.infer<typeof FileKindSchema>;

export const OUTPUT_FILE_TYPES = {
  xlsx: {
    mediaType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    kind: 'spreadsheet',
  },
  csv: { mediaType: 'text/csv', kind: 'spreadsheet' },
  tsv: { mediaType: 'text/tab-separated-values', kind: 'spreadsheet' },
  pdf: { mediaType: 'application/pdf', kind: 'document' },
  docx: {
    mediaType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    kind: 'document',
  },
  md: { mediaType: 'text/markdown', kind: 'document' },
  txt: { mediaType: 'text/plain', kind: 'document' },
  json: { mediaType: 'application/json', kind: 'data' },
  ndjson: { mediaType: 'application/x-ndjson', kind: 'data' },
  parquet: { mediaType: 'application/vnd.apache.parquet', kind: 'data' },
} as const satisfies Record<string, { mediaType: string; kind: FileKind }>;

export const FALLBACK_MEDIA_TYPE = 'application/octet-stream';

export const CHART_SPEC_SUFFIX = '.chart.json';

export const CHART_SPEC_MEDIA_TYPE = 'application/json';

export function isChartSpecFileName(filename: string): boolean {
  return (
    filename.toLowerCase().endsWith(CHART_SPEC_SUFFIX) && filename.length > CHART_SPEC_SUFFIX.length
  );
}

export function fileExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot + 1).toLowerCase();
}

export function isOutputFileName(filename: string): boolean {
  return Object.hasOwn(OUTPUT_FILE_TYPES, fileExtension(filename));
}

export function describeFileName(filename: string): { mediaType: string; kind: FileKind } {
  if (isChartSpecFileName(filename))
    return { mediaType: CHART_SPEC_MEDIA_TYPE, kind: 'chart-spec' };
  const extension = fileExtension(filename);
  const known = Object.hasOwn(OUTPUT_FILE_TYPES, extension)
    ? (OUTPUT_FILE_TYPES as Record<string, { mediaType: string; kind: FileKind }>)[extension]
    : undefined;
  return known ?? { mediaType: FALLBACK_MEDIA_TYPE, kind: 'other' };
}
